import { addDays, differenceInCalendarDays, startOfDay } from 'date-fns'
import { FC, memo, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Block } from '@mui/icons-material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { LockedDataIndicator } from '../../../../components/LockedDataIndicator/LockedDataIndicator'
import { TrendIndicator } from '../../../../components/TrendIndicator/TrendIndicator'
import { ValueIndicator, ValueType } from '../../../../components/ValueIndicator/ValueIndicator'
import { useDateTimeFormatter } from '../../../live-events/hooks/useDateTimeFormatter'
import { UPDATE_IMPACTS_SINCE, UPDATE_IMPACT_DATA_LAG_DAYS } from '../../../update-history/constants/constants'
import { ComparisonInterval } from '../../types/ComparisonInterval'

/**
 * Number of calendar days that must elapse between the version release and
 * "today" before the N-day impact aggregate has all N days of post-update
 * data available. Equals the comparison interval plus the daily Rev/DL data
 * lag offset (`UPDATE_IMPACT_DATA_LAG_DAYS`, currently `1`).
 *
 * The +1 offset reflects the typical 2-day Rev/DL data lag combined with the
 * inclusive day-count semantics: an N-day aggregate that includes the release
 * day needs `latest_data_date >= release + (N - 1)`, i.e. `release + N` must be
 * no later than `today - data_lag + 1`. With data_lag = 2 that collapses to a
 * +1 offset on top of N. (GR-2424)
 */
export const getImpactDataAvailableAfterDays = (comparisonInterval: ComparisonInterval): number => parseInt(comparisonInterval) + UPDATE_IMPACT_DATA_LAG_DAYS

/**
 * Determines if a version release is too fresh to expose its N-day impact
 * aggregate (i.e. fewer than N days of post-release Rev/DL data are available
 * once the typical 2-day data lag is taken into account).
 *
 * The comparison is done at **calendar-day** granularity:
 *   `tooFresh ⇔ calendarDaysSinceRelease < N + 1`
 *
 * Crucially the inputs are NOT compared as raw timestamps. `releaseDate` is a
 * backend timestamp that carries a time-of-day component (the moment the
 * version was released), and `new Date()` is the intraday "now". Comparing the
 * raw values made the N-day boundary depend on the release time-of-day vs the
 * current time-of-day, hiding a boundary version in the morning and showing it
 * in the afternoon. `differenceInCalendarDays` normalises both sides to the
 * start of their day so the boundary is deterministic — matching the
 * `startOfDay`-based gate already used in live events. (GR-2424)
 *
 * Boundary examples (today = 2026-05-29, latest Rev/DL data = May 27):
 *   - 7-day:  release ≤ May 21 → visible, May 22 → tooFresh
 *   - 14-day: release ≤ May 14 → visible, May 15 → tooFresh
 *   - 30-day: release ≤ Apr 28 → visible, Apr 29 → tooFresh
 */
export const isVersionTooFresh = (releaseDate: number, comparisonInterval?: ComparisonInterval): boolean => {
  if (!comparisonInterval) return false
  const requiredDays = getImpactDataAvailableAfterDays(comparisonInterval)
  return differenceInCalendarDays(new Date(), new Date(releaseDate)) < requiredDays
}

/**
 * Component for displaying aggregate values for revenue, downloads and their ratio
 */
type AggregateValueProps = {
  value: number | undefined
  trendValue: number
  type: ValueType
  releaseDate: number
  daysSinceReleased: number
  comparisonInterval?: ComparisonInterval
  maxTrendValue?: number
  isFirstVersion?: boolean
  isTooOld?: boolean
  isBeforeGlobalLaunch?: boolean
  isAvailableForCurrentMarket?: boolean
  isUnlocked?: boolean
}

export const AggregateValue: FC<AggregateValueProps> = memo(
  ({
    value,
    trendValue,
    type = ValueType.Number,
    releaseDate,
    daysSinceReleased,
    comparisonInterval = ComparisonInterval.DAYS_7,
    maxTrendValue,
    isFirstVersion,
    isTooOld,
    isBeforeGlobalLaunch,
    isAvailableForCurrentMarket,
    isUnlocked,
  }) => {
    if (!isUnlocked) {
      return <LockedDataIndicator />
    }

    const requiredDays = getImpactDataAvailableAfterDays(comparisonInterval)
    return (
      <div>
        <ExceptionValue
          isTooFresh={isVersionTooFresh(releaseDate, comparisonInterval)}
          isFirstVersion={isFirstVersion}
          isTooOld={isTooOld}
          isBeforeGlobalLaunch={isBeforeGlobalLaunch}
          isAvailableForCurrentMarket={isAvailableForCurrentMarket}
          tsWhenAvailable={addDays(startOfDay(new Date(releaseDate)), requiredDays).getTime()}
        >
          <TrendIndicator value={trendValue} direction maxValue={maxTrendValue} />
          {value === undefined ? <UnavailableValue /> : <ValueIndicator value={value} type={type} maximumFractionDigits={2} shorten sign />}
        </ExceptionValue>
      </div>
    )
  }
)

/**
 * Renders an explicit "no data" indicator for the aggregate value when the
 * underlying calculation cannot produce a value (e.g. RV/DL change when
 * exactly one period has zero downloads — division by zero). Wrapped in a
 * block-level span so the icon appears below the trend percentage, matching
 * the layout used by `ValueIndicator` (which is `display: block`).
 */
const UnavailableValue: FC = () => {
  const { t } = useTranslation()
  return (
    <span style={{ display: 'block' }}>
      <GRTooltip content={t('common:no_data_available')}>
        <Block data-testid="aggregate-value-unavailable" fontSize="small" color="disabled" />
      </GRTooltip>
    </span>
  )
}

type ExceptionValueProps = {
  children?: ReactNode
  isFirstVersion?: boolean
  isTooOld?: boolean
  isBeforeGlobalLaunch?: boolean
  isTooFresh?: boolean
  isAvailableForCurrentMarket?: boolean
  tsWhenAvailable: number
}

const ExceptionValue: FC<ExceptionValueProps> = ({
  tsWhenAvailable,
  isFirstVersion,
  isTooOld,
  isBeforeGlobalLaunch,
  isTooFresh,
  isAvailableForCurrentMarket,
  children,
}) => {
  const { t } = useTranslation()
  const dateFormatter = useDateTimeFormatter({ month: 'short', day: 'numeric', year: 'numeric' })

  if (isTooFresh) {
    return (
      <GRTooltip
        content={t('common:impact_data_available_tooltip', {
          date: dateFormatter.format(tsWhenAvailable),
        })}
      >
        <AccessTimeIcon fontSize="small" color="disabled" />
      </GRTooltip>
    )
  }

  if (isFirstVersion) {
    return (
      <GRTooltip content={t('common:update_impacts_not_available_for_first_version')}>
        <Block fontSize="small" color="disabled" />
      </GRTooltip>
    )
  }

  if (isTooOld) {
    return (
      <GRTooltip
        content={t('common:update_impacts_available_date_description', {
          date: dateFormatter.format(UPDATE_IMPACTS_SINCE),
        })}
      >
        <Block fontSize="small" color="disabled" />
      </GRTooltip>
    )
  }

  if (!isAvailableForCurrentMarket) {
    return (
      <GRTooltip content={t('common:update_impacts_not_available_text')}>
        <Block fontSize="small" color="disabled" />
      </GRTooltip>
    )
  }

  if (isBeforeGlobalLaunch) {
    return (
      <GRTooltip content={t('common:update_impact_version_not_available_for_market_text')}>
        <Block fontSize="small" color="disabled" />
      </GRTooltip>
    )
  }

  return <>{children}</>
}
